import React, { useState, useEffect } from "react";
import apiClient from "../../apiClient";
import imageCompression from "browser-image-compression"; // Import the compression library
import { useNavigate } from "react-router-dom";
import Logout from "./Logout";
import BackButton from "./BackButton";
import styles from "./modules/AddProduct.module.scss";

const AddProduct = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [primaryImageUrl, setPrimaryImageUrl] = useState(""); // URL from S3
  const [categoryId, setCategoryId] = useState("");
  const [categories, setCategories] = useState([]);
  const [additionalInfo, setAdditionalInfo] = useState([
    { description: "", value: "" },
  ]);
  const [secondaryImages, setSecondaryImages] = useState([]);
  const navigate = useNavigate();

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await apiClient.get("/admin/categories");
        setCategories(res.data);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // Compress and upload the primary image to S3
  const handlePrimaryImageUpload = async (e) => {
    const file = e.target.files[0];

    // Compression options
    const options = { maxSizeMB: 1, maxWidthOrHeight: 800, useWebWorker: true };

    try {
      const compressedFile = await imageCompression(file, options);

      const formData = new FormData();
      formData.append("image", compressedFile);

      const res = await apiClient.post("/admin/upload-image", formData);
      setPrimaryImageUrl(res.data.imageUrl); // Store the S3 URL of the uploaded image
    } catch (err) {
      console.error("Error uploading compressed image:", err);
    }
  };

  // Compress and upload secondary images to S3
  const handleSecondaryImageUpload = async (e, index) => {
    const file = e.target.files[0];

    const options = { maxSizeMB: 1, maxWidthOrHeight: 800, useWebWorker: true };

    try {
      const compressedFile = await imageCompression(file, options);

      const formData = new FormData();
      formData.append("image", compressedFile);

      const res = await apiClient.post("/admin/upload-image", formData);

      const newSecondaryImages = [...secondaryImages];
      newSecondaryImages[index].image_url = res.data.imageUrl;
      setSecondaryImages(newSecondaryImages);
    } catch (err) {
      console.error("Error uploading secondary image:", err);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const productData = {
        name,
        description,
        primary_picture: primaryImageUrl,
        category_id: categoryId,
        additional_info: additionalInfo,
        secondary_images: secondaryImages,
      };

      const res = await apiClient.post("/admin/products", productData);
      navigate("/admin/products");
    } catch (err) {
      console.error("Error creating product:", err);
    }
  };

  // Handle dynamic fields for Additional Info
  const handleAddAdditionalInfo = () => {
    setAdditionalInfo([...additionalInfo, { description: "", value: "" }]);
  };

  const handleRemoveAdditionalInfo = (index) => {
    const newAdditionalInfo = additionalInfo.filter((_, i) => i !== index);
    setAdditionalInfo(newAdditionalInfo);
  };

  const handleAdditionalInfoChange = (index, field, value) => {
    const newAdditionalInfo = [...additionalInfo];
    newAdditionalInfo[index][field] = value;
    setAdditionalInfo(newAdditionalInfo);
  };

  // Handle dynamic fields for Secondary Images
  const handleAddSecondaryImage = () => {
    setSecondaryImages([...secondaryImages, { image_url: "" }]);
  };

  const handleRemoveSecondaryImage = (index) => {
    const newSecondaryImages = secondaryImages.filter((_, i) => i !== index);
    setSecondaryImages(newSecondaryImages);
  };

  return (
    <div className={styles.addProductContainer}>
      <h1 className={styles.title}>Add New Product</h1>
      <Logout />
      <BackButton to="/admin/products" />

      <form onSubmit={handleSubmit} className={styles.form}>
        <label>Product Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <label>Product Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        ></textarea>

        <label>Primary Picture</label>
        <input type="file" onChange={handlePrimaryImageUpload} required />
        {primaryImageUrl && (
          <div className={styles.imageContainer}>
            <img
              src={primaryImageUrl}
              alt="Primary"
              className={styles.imagePreview}
            />
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => setPrimaryImageUrl("")}
            >
              Remove
            </button>
          </div>
        )}

        <label>Category</label>
        <select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          required
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>

        <h2>Additional Information</h2>
        {additionalInfo.map((info, index) => (
          <div key={index} className={styles.additionalInfoItem}>
            <input
              type="text"
              placeholder="Description"
              value={info.description}
              onChange={(e) =>
                handleAdditionalInfoChange(index, "description", e.target.value)
              }
              required
            />
            <input
              type="text"
              placeholder="Value"
              value={info.value}
              onChange={(e) =>
                handleAdditionalInfoChange(index, "value", e.target.value)
              }
              required
            />
            {additionalInfo.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemoveAdditionalInfo(index)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <button type="button" onClick={handleAddAdditionalInfo}>
          Add More Info
        </button>

        <h2>Secondary Images</h2>
        {secondaryImages.map((image, index) => (
          <div key={index} className={styles.secondaryImageItem}>
            <input
              type="file"
              onChange={(e) => handleSecondaryImageUpload(e, index)}
            />
            {image.image_url && (
              <div className={styles.imageContainer}>
                <img
                  src={image.image_url}
                  alt={`Secondary ${index}`}
                  className={styles.imagePreview}
                />
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => handleRemoveSecondaryImage(index)}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}
        <button type="button" onClick={handleAddSecondaryImage}>
          Add More Image
        </button>

        <button type="submit">Submit</button>
      </form>
    </div>
  );
};

export default AddProduct;
